import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {PullRequestErrorState} from '../components/PullRequestErrorState'
import {
  pullRequestsChangesRoute,
  pullRequestsChangesWithRangeRoute,
  pullRequestsFilesRoute,
  pullRequestsFilesWithRangeRoute,
} from './files-changed-routes'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from './layout-route'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import {Suspense, useEffect} from 'react'
import type {PullRequest} from '../types'
import type {FilesRoutePayload} from './route-payload-types'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {usePullRequestAnalytics} from '../hooks/use-pull-request-analytics'
import {useMemoryStats} from '@github-ui/use-memory-stats'
import {getCurrentDiffImplementation} from '../helpers/get-diff-implementation'
import {DiffComparisonViewer} from '../components/DiffComparisonViewer'

export function ChangesEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <Changes />
    </ErrorBoundary>
  )
}

export function ChangesWithRangeEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="ChangesWithRangeEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <ChangesWithRange />
    </ErrorBoundary>
  )
}

function Changes() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesRoute, 'mainQuery')

  return (
    <FilesChangedComponent
      pullRequest={pullRequest}
      routeData={routeData}
      // force remount when routeData updates after soft navigation so the component gets fresh data
      key={`${routeData.pullRequest.comparison.baseOid}-${routeData.pullRequest.comparison.headOid}`}
    />
  )
}

function ChangesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsChangesWithRangeRoute, 'mainQuery')
  const {range} = useRouteParams(pullRequestsChangesWithRangeRoute)

  return <FilesChangedComponent pullRequest={pullRequest} routeData={routeData} key={range} />
}

export function FilesEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="FilesEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <Files />
    </ErrorBoundary>
  )
}

export function FilesWithRangeEntrypoint() {
  return (
    <ErrorBoundary
      critical
      boundaryName="FilesWithRangeEntrypoint"
      fallback={<PullRequestErrorState text="Changes cannot be loaded" />}
    >
      <FilesWithRange />
    </ErrorBoundary>
  )
}

function Files() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsFilesRoute, 'mainQuery')

  return (
    <Suspense>
      <FilesChangedComponent
        pullRequest={pullRequest}
        routeData={routeData}
        // force remount when routeData updates after soft navigation so the component gets fresh data
        key={`${routeData.pullRequest.comparison.baseOid}-${routeData.pullRequest.comparison.headOid}`}
      />
    </Suspense>
  )
}

function FilesWithRange() {
  const {
    data: {pullRequest},
  } = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: routeData} = useRouteQuery(pullRequestsFilesWithRangeRoute, 'mainQuery')
  const {range} = useRouteParams(pullRequestsFilesWithRangeRoute)

  return (
    <Suspense>
      <FilesChangedComponent pullRequest={pullRequest} routeData={routeData} key={range} />
    </Suspense>
  )
}

function FilesAnalytics() {
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  useEffect(() => {
    sendPullRequestAnalyticsEvent('page_view.files_page', 'PAGE_VIEW', {
      browser_width: typeof window !== 'undefined' ? window.innerWidth : 0,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}

export function FilesChangedComponent({
  pullRequest,
  routeData,
}: {
  pullRequest: PullRequest
  routeData: FilesRoutePayload
}) {
  const diffImplementation = getCurrentDiffImplementation(routeData.virtualizeDiffEntries)
  useMemoryStats({
    statName: 'PULL_REQUESTS_FILES_JS_HEAP',
    tags: {
      version: diffImplementation.version,
      virtualized: String(diffImplementation.virtualized),
    },
  })

  return (
    <AnalyticsProvider
      appName="pull-request-files"
      category="files_tab"
      metadata={{
        pull_request_id: pullRequest.id.toString(),
        base_sha: routeData.pullRequest.comparison.baseOid,
        head_sha: routeData.pullRequest.comparison.headOid,
        diff_version: diffImplementation.version,
        diff_virtualized: String(diffImplementation.virtualized),
      }}
    >
      <FilesAnalytics />
      <Suspense>
        <DiffComparisonViewer.Viewer
          basePath={pullRequest.pathName}
          initialData={routeData}
          useAdvancedToolbar
          useChangeGroups
          useInlineComments
          useEditOptions
          useFileTree
        />
      </Suspense>
    </AnalyticsProvider>
  )
}

try{ ChangesEntrypoint.displayName ||= 'ChangesEntrypoint' } catch {}
try{ ChangesWithRangeEntrypoint.displayName ||= 'ChangesWithRangeEntrypoint' } catch {}
try{ Changes.displayName ||= 'Changes' } catch {}
try{ ChangesWithRange.displayName ||= 'ChangesWithRange' } catch {}
try{ FilesEntrypoint.displayName ||= 'FilesEntrypoint' } catch {}
try{ FilesWithRangeEntrypoint.displayName ||= 'FilesWithRangeEntrypoint' } catch {}
try{ Files.displayName ||= 'Files' } catch {}
try{ FilesWithRange.displayName ||= 'FilesWithRange' } catch {}
try{ FilesAnalytics.displayName ||= 'FilesAnalytics' } catch {}
try{ FilesChangedComponent.displayName ||= 'FilesChangedComponent' } catch {}